import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  Chip,
  Collapse,
  IconButton
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoIcon from '@mui/icons-material/Info';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import './SmartInsights.css';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const SmartInsights = ({ employees, employeeWorkload }) => {
  const [expandedInsights, setExpandedInsights] = React.useState({});

  const toggleInsight = (key) => {
    setExpandedInsights(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Calculate insights
  const insights = useMemo(() => {
    const results = [];

    // Get workload level for each employee
    const getWorkloadLevel = (workload) => {
      if (workload === 0) return 'none';
      if (workload <= 5) return 'light';
      if (workload <= 15) return 'medium';
      return 'heavy';
    };

    // 1. Heavy Workload Warning
    const heavyWorkloadEmployees = employees.filter(emp => {
      const workload = employeeWorkload[emp.id] || 0;
      return getWorkloadLevel(workload) === 'heavy';
    });

    if (heavyWorkloadEmployees.length > 0) {
      results.push({
        key: 'heavyWorkload',
        type: 'warning',
        icon: <WarningAmberIcon />,
        title: 'Heavy Workload Alert',
        message: `${heavyWorkloadEmployees.length} employee(s) have heavy workload - consider redistribution`,
        details: `Employees: ${heavyWorkloadEmployees.map(e => e.name || `${e.empObject?.firstName || ''} ${e.empObject?.lastName || ''}`.trim() || 'Unknown').join(', ')}`,
        severity: 'warning'
      });
    }

    // 2. Coverage Gap Analysis
    const coverageByDay = DAYS_OF_WEEK.map(day => ({
      day,
      count: employees.filter(emp => emp.empObject?.serviceDay === day).length
    }));

    const maxCoverage = Math.max(...coverageByDay.map(d => d.count));
    const minCoverage = Math.min(...coverageByDay.map(d => d.count));
    const gapDays = coverageByDay.filter(d => d.count < maxCoverage * 0.5);

    if (gapDays.length > 0) {
      const maxDay = coverageByDay.find(d => d.count === maxCoverage);
      results.push({
        key: 'coverageGap',
        type: 'info',
        icon: <CalendarTodayIcon />,
        title: 'Coverage Gap Detected',
        message: `${maxDay.day} has the most coverage (${maxCoverage} employees) - ${gapDays.map(d => d.day).join(', ')} need${gapDays.length > 1 ? '' : 's'} more employees`,
        details: `Consider reassigning employees to balance coverage across the week`,
        severity: 'info'
      });
    }

    // 3. Performance Highlights
    const employeesWithWorkload = employees
      .map(emp => ({
        ...emp,
        workload: employeeWorkload[emp.id] || 0
      }))
      .filter(emp => emp.workload > 0);

    if (employeesWithWorkload.length > 0) {
      const avgWorkload = employeesWithWorkload.reduce((sum, emp) => sum + emp.workload, 0) / employeesWithWorkload.length;
      const topPerformers = employeesWithWorkload.filter(emp => emp.workload > avgWorkload * 1.15);

      if (topPerformers.length > 0) {
        const topPerformer = topPerformers[0];
        const percentAbove = Math.round(((topPerformer.workload - avgWorkload) / avgWorkload) * 100);
        const empName = topPerformer.name || `${topPerformer.empObject?.firstName || ''} ${topPerformer.empObject?.lastName || ''}`.trim() || 'Top Employee';

        results.push({
          key: 'topPerformer',
          type: 'success',
          icon: <TrendingUpIcon />,
          title: 'Top Performer',
          message: `${empName} completed ${percentAbove}% more tasks than average`,
          details: `Total routes: ${topPerformer.workload} (Average: ${Math.round(avgWorkload)})`,
          severity: 'success'
        });
      }
    }

    // 4. Unassigned Employees
    const unassignedEmployees = employees.filter(emp => {
      const workload = employeeWorkload[emp.id] || 0;
      return workload === 0;
    });

    if (unassignedEmployees.length > 0) {
      results.push({
        key: 'unassigned',
        type: 'warning',
        icon: <PersonOffIcon />,
        title: 'Unassigned Employees',
        message: `${unassignedEmployees.length} employee(s) haven't been assigned routes`,
        details: `Employees: ${unassignedEmployees.slice(0, 5).map(e => e.name || `${e.empObject?.firstName || ''} ${e.empObject?.lastName || ''}`.trim() || 'Unknown').join(', ')}${unassignedEmployees.length > 5 ? ` and ${unassignedEmployees.length - 5} more` : ''}`,
        severity: 'warning'
      });
    }

    // 5. Workload Distribution Analysis
    const lightWorkloadEmployees = employees.filter(emp => {
      const workload = employeeWorkload[emp.id] || 0;
      return getWorkloadLevel(workload) === 'light' && workload > 0;
    });

    const mediumWorkloadEmployees = employees.filter(emp => {
      const workload = employeeWorkload[emp.id] || 0;
      return getWorkloadLevel(workload) === 'medium';
    });

    if (lightWorkloadEmployees.length > 0 && heavyWorkloadEmployees.length > 0) {
      results.push({
        key: 'redistribution',
        type: 'info',
        icon: <InfoIcon />,
        title: 'Redistribution Opportunity',
        message: `${lightWorkloadEmployees.length} employee(s) with light workload could take on more tasks`,
        details: `This could help balance the load from ${heavyWorkloadEmployees.length} overloaded employee(s)`,
        severity: 'info'
      });
    }

    return results;
  }, [employees, employeeWorkload]);

  if (insights.length === 0) {
    return (
      <Box sx={{ mt: 3, mb: 3 }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
            textAlign: 'center'
          }}
        >
          <TrendingUpIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: '#10B981', mb: 1 }} />
          <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            All Systems Optimal
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Workload is well-balanced across all employees. No action required at this time.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: '#003366',
          mb: 2,
          fontSize: { xs: '1.25rem', sm: '1.5rem' }
        }}
      >
        Smart Insights & Recommendations
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
        {insights.map((insight) => (
          <Alert
            key={insight.key}
            severity={insight.severity}
            icon={insight.icon}
            sx={{
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%'
              },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
            action={
              insight.details && (
                <IconButton
                  aria-label="expand"
                  size="small"
                  onClick={() => toggleInsight(insight.key)}
                  sx={{ mt: -0.5 }}
                >
                  {expandedInsights[insight.key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )
            }
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              {insight.title}
            </AlertTitle>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {insight.message}
            </Typography>

            {insight.details && (
              <Collapse in={expandedInsights[insight.key]} timeout="auto">
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                  <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                    {insight.details}
                  </Typography>
                </Box>
              </Collapse>
            )}
          </Alert>
        ))}
      </Box>
    </Box>
  );
};

export default SmartInsights;
